import { useCallback, useEffect, useRef, useState } from 'react';
import { getMemoryChapters } from '../../lib/use-cases/get-memory-chapters';
import { openMemoryChat } from '../../lib/use-cases/open-memory-chat';
import { submitMemoryAnswer } from '../../lib/use-cases/submit-memory-answer';
import { logError } from '../../lib/shared/error-logger';
import { logEvent } from '../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../lib/shared/event-types';

const MEMORIES_PER_CHAPTER = 5;
const RATE_LIMIT_CODE = 'TooManyRequests';
const GENERIC_SEND_ERROR = 'Something went wrong while saving your memory. Please try again.';

function useMemoryKeeper({ onTabChange }) {
    const [chapters, setChapters] = useState([]);
    const [isLoadingChapters, setIsLoadingChapters] = useState(true);
    const [errorState, setErrorState] = useState({ chapters: false, chat: null });
    const [activeChapterId, setActiveChapterId] = useState(null);
    const [messagesByChapter, setMessagesByChapter] = useState({});
    const [chatFinishedByChapter, setChatFinishedByChapter] = useState({});
    const [loadingChatChapterId, setLoadingChatChapterId] = useState(null);
    const [typingChapterId, setTypingChapterId] = useState(null);
    const [sendErrorMessage, setSendErrorMessage] = useState(null);
    const loadedChatChapterIdsRef = useRef(new Set());

    useEffect(() => {
        async function loadChapters() {
            setIsLoadingChapters(true);
            setErrorState((prev) => ({ ...prev, chapters: false }));

            try {
                const data = await getMemoryChapters();
                const initialChapter = data.find((chapter) => chapter.isInitial);

                setChapters(data);
                setActiveChapterId((current) => current || initialChapter?.id || data[0]?.id || null);
            } catch (error) {
                logError(error);
                logEvent(EVENT_TYPES.MEMORY_KEEPER_CHAPTERS_LOAD_ERROR_VIEWED);
                setErrorState((prev) => ({ ...prev, chapters: true }));
            } finally {
                setIsLoadingChapters(false);
            }
        }

        loadChapters();
    }, []);

    const loadChat = useCallback(async (chapterId) => {
        setLoadingChatChapterId(chapterId);
        setErrorState((prev) => ({ ...prev, chat: null }));

        try {
            const chat = await openMemoryChat(chapterId);

            loadedChatChapterIdsRef.current.add(chapterId);
            setMessagesByChapter((prev) => ({ ...prev, [chapterId]: chat.messages }));
            setChatFinishedByChapter((prev) => ({ ...prev, [chapterId]: chat.isChatFinished }));
        } catch (error) {
            logError(error);
            logEvent(EVENT_TYPES.MEMORY_KEEPER_CHAT_LOAD_ERROR_VIEWED, { 'chapter_id': chapterId });
            setErrorState((prev) => ({ ...prev, chat: chapterId }));
        } finally {
            setLoadingChatChapterId(null);
        }
    }, []);

    useEffect(() => {
        if (!activeChapterId || loadedChatChapterIdsRef.current.has(activeChapterId)) {
            return;
        }

        loadChat(activeChapterId);
    }, [activeChapterId, loadChat]);

    const refreshChapters = useCallback(async () => {
        try {
            const data = await getMemoryChapters();

            setChapters(data);
        } catch (error) {
            logError(error);
        }
    }, []);

    const handleSelectChapter = useCallback((chapterId) => {
        logEvent(EVENT_TYPES.MEMORY_KEEPER_CHAPTER_SELECTED, { 'chapter_id': chapterId });
        setActiveChapterId(chapterId);
        onTabChange('chat');
    }, [onTabChange]);

    const handleSendMessage = useCallback(async (text) => {
        const chapterId = activeChapterId;

        if (!chapterId) {
            return;
        }

        const userMessage = { id: `msg-${Date.now()}`, author: 'user', text };

        setSendErrorMessage(null);

        setMessagesByChapter((prev) => ({
            ...prev,
            [chapterId]: [...(prev[chapterId] || []), userMessage]
        }));

        setChapters((prev) => prev.map((chapter) => (
            chapter.id === chapterId
                ? { ...chapter, answerCount: chapter.answerCount + 1, answersUntilMemory: Math.max(chapter.answersUntilMemory - 1, 0) }
                : chapter
        )));

        setTypingChapterId(chapterId);

        try {
            const chat = await submitMemoryAnswer(chapterId, text);

            setMessagesByChapter((prev) => ({ ...prev, [chapterId]: chat.messages }));
            setChatFinishedByChapter((prev) => ({ ...prev, [chapterId]: chat.isChatFinished }));
            refreshChapters();
        } catch (error) {
            logError(error);
            setSendErrorMessage(error?.code === RATE_LIMIT_CODE ? error.message : (error?.message || GENERIC_SEND_ERROR));
        } finally {
            setTypingChapterId((current) => (current === chapterId ? null : current));
        }
    }, [activeChapterId, refreshChapters]);

    const activeChapter = chapters.find((chapter) => chapter.id === activeChapterId) || null;
    const messages = activeChapterId ? (messagesByChapter[activeChapterId] || []) : [];
    const isLoadingMessages = Boolean(activeChapterId) && loadingChatChapterId === activeChapterId;
    const isChatError = Boolean(activeChapterId) && errorState.chat === activeChapterId;
    const isChatFinished = Boolean(activeChapterId && chatFinishedByChapter[activeChapterId]);

    return {
        chapters,
        isLoadingChapters,
        isChaptersError: errorState.chapters,
        activeChapter,
        messages,
        isLoadingMessages,
        isChatError,
        isChatFinished,
        isAssistantTyping: typingChapterId === activeChapterId,
        sendErrorMessage,
        max: MEMORIES_PER_CHAPTER,
        handleSelectChapter,
        handleSendMessage
    };
}

export {
    useMemoryKeeper
};
